/** @jsx jsx */

import React, { useRef, useEffect, useLayoutEffect } from 'react';
import { css, jsx } from '@emotion/core';
import useIntersectionObserver from 'hooks/useIntersectionObserver';
import { useSelector } from 'react-redux';
import arrow from 'assets/pointer_dark.svg';
import { colors, getButtonFocus } from 'styles/variables';
import getStore from 'services/getStore';
import getInteractionsInTopic from 'services/apiHelpers/getInteractionsInTopic';
import getAllInteractions from 'services/apiHelpers/getAllInteractions';
import getTopics from 'services/apiHelpers/getTopics';
import setCurrentTopic from 'services/apiHelpers/setCurrentTopic';
import isIE from 'services/isIE';
import styled from '@emotion/styled';
import { VisuallyHidden } from 'components/UI/VisuallyHidden';
import pubSub from 'services/pubSub';
import onScrollEnds from 'services/onScrollEnds';
import getSectionInView from 'services/getSectionInView';

const store = getStore();
const { lPink, lGrey2 } = colors;

let firstSection;
let scrolling;

const Arrow = ({ color }) => {
  return (
    <svg width="9px" height="15px" viewBox="0 0 9 15">
      <g stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
        <g
          transform="translate(-1206.000000, -566.000000)"
          stroke={color}
          strokeWidth="2"
        >
          <polyline
            transform="translate(1210.000000, 573.500000) rotate(-90.000000) translate(-1210.000000, -573.500000)"
            points="1216.5 570.5 1210 576.5 1203.5 570.5"
          />
        </g>
      </g>
    </svg>
  );
};

const navToPrevTopic = () => {
  const { currentTopic } = store.getState();
  const topics = getTopics();
  const prevTopic = topics[topics.findIndex(t => t.id === currentTopic) - 1];

  if (!prevTopic) return;

  setCurrentTopic(prevTopic.id);

  requestAnimationFrame(() => window.scrollTo(0, document.body.scrollHeight));
};

const BackBtn = ({ currentTopic, topics, color }) => {
  const backBtn = useRef(null);

  useEffect(() => {
    if (window.dev()) {
      return;
    }

    // if we're not in the first topic. return;
    if (topics.findIndex(t => t.id === currentTopic.id) !== 0) {
      return;
    }

    if (!firstSection) {
      firstSection = document.querySelector('section');
      if (!firstSection) {
        return;
      }
    }

    const onScroll = () => {
      clearTimeout(scrolling);

      scrolling = setTimeout(() => {
        const { top } = firstSection.getBoundingClientRect();

        if (top < 0) {
          backBtn.current.style.opacity = 1;
          backBtn.current.style.pointerEvents = 'auto';
        } else {
          backBtn.current.style.opacity = 0.1;
          backBtn.current.style.pointerEvents = 'none';
        }
      }, 100);
    };

    onScroll();
    window.addEventListener('scroll', onScroll);

    return () => {
      firstSection = null;
      window.removeEventListener('scroll', onScroll);
    };
  }, [currentTopic]);

  useEffect(() => {
    pubSub.subscribe('HILITE_CHAMELEON_BACK_BUTTON', ({ toggle }) => {
      backBtn.current.style.boxShadow = toggle
        ? `0 0 0 2px #fff, 0 0 0 3px ${color}`
        : null;
    });
  }, []);

  const onClick = () => {
    if (window.locationHistory) {
      const locationHistory = window.locationHistory;

      window.location.hash = locationHistory; // window.locationHistory;

      const checkHistory = setInterval(() => {
        if (locationHistory !== window.locationHistory) {
          clearInterval(checkHistory);

          requestAnimationFrame(
            () =>
              (window.location.hash = Math.random()
                .toString(16)
                .substr(2, 8))
          );

          window.locationHistory = null;
        }
      }, 1000 / 60);

      return;
    }
    //

    if (isIE()) {
      const section = document.querySelector('section');
      const { top } = section.getBoundingClientRect();
      const bounds = window.innerHeight * 0.6 * -1;
      if (top === 0) {
        navToPrevTopic();
        return;
      }
    }

    // need a new way to get prev section that isn't based on intersectionObserver
    // const sections = Array.from(document.querySelectorAll('section'));
    // const closestSection = sections.find((section: any) => {
    //   const bounds = section.getBoundingClientRect();
    //   if (bounds.top >= 0 && bounds.top <= window.innerHeight / 2) {
    //     return section;
    //   }
    // });

    const { sections, section, index } = getSectionInView();

    const prevSection = sections[index - 1];
    // sections[sections.indexOf(closestSection) - 1];

    if (prevSection) {
      prevSection.scrollIntoView({
        behavior: 'smooth',
      });

      onScrollEnds(() => {
        prevSection.focus();
      });
    } else {
      navToPrevTopic();
    }

    // prevSection
    //   ? prevSection.scrollIntoView({
    //       behavior: 'smooth',
    //     })
    //   : navToPrevTopic();
  };

  return (
    <a
      ref={node => {
        if (!backBtn.current) backBtn.current = node;
      }}
      id="chameleon-back-button"
      // href="#go-back"
      title="Return to previous interaction"
      css={css`
        position: relative;
        display: flex;
        justify-content: center;
        align-items: center;
        flex-shrink: 0;
        width: 30px;
        height: 30px;
        border-radius: 50%;
        transform: scaleX(-1);
        transition: opacity: 0.2s;
        cursor: pointer;
        -webkit-backface-visibility: hidden;
        transition: all 0.2s;

        &:hover {
          background-color: ${lGrey2};
        }

        &:focus {
          ${getButtonFocus(color)}
        }
    `}
      onKeyUp={e => {
        return;

        if (![' ', 'Enter'].includes(e.key)) {
          return;
        }
        // console.log('be like button !!!');

        onClick();
      }}
      onClick={onClick}
    >
      <VisuallyHidden>Go Back</VisuallyHidden>
      <Arrow color={color} />
    </a>
  );
};

export default BackBtn;
